/**
 * useHydra — React hook that manages the HydraClient lifecycle.
 * Starts on mount, stops on unmount. Feeds data into the Zustand marketStore.
 *
 * Multi-feed (V3 satellite hydras): g1 consumes the firehose hydra AND any
 * configured hydraedge pipes at the same time (see feedConfig.ts). One
 * HydraClient per feed — each its own SharedWorker instance / upstream socket —
 * all draining into the single, source-agnostic marketStore. The firehose is
 * the "primary": only it drives the global connected / backpressure flags and
 * owns the orderbook-needs subscription (edge orderbook routing is a follow-up;
 * the Live Discovery view needs only market frames). Each feed ingests only the
 * sports it owns (feedConfig.accepts), so a sport served by both pipes — e.g.
 * MLB on a multi-sport edge node — doesn't flap between sources.
 *
 * Consumer WS notes: Hydra's consumer WS is on the API port (8891), not :443
 * (the public dashboard). Bearer auth on the socket isn't available from the
 * browser (no custom headers) — see HydraClient. Default firehose URL lives in
 * feedConfig.
 */

import { useEffect, useRef } from 'react';
import { HydraClient, type HydraCallbacks } from '../clients/HydraClient';
import { resolveFeeds, type FeedConfig } from '../clients/feedConfig';
import { useMarketStore, normalizeSport } from '../stores/marketStore';

// Resolved once at module load — env is build-time static.
const FEEDS = resolveFeeds();

// Staleness thresholds for the resnapshot triggers. Background tabs get
// throttled by the browser (Chrome heavily after ~5 min) and the WS can
// die silently — `connected: true` plus a flat `lastMessageMs` is the
// signature. Visibility-return resnaps when we've been quiet for 30 s+
// (catches "I came back to the tab"); the watchdog fires at 60 s+ for
// the rare case where the tab stayed visible but went silent anyway.
const VISIBILITY_RESNAP_STALE_MS = 30_000;
const WATCHDOG_STALE_MS = 60_000;
const WATCHDOG_INTERVAL_MS = 30_000;

// Click-to-refresh: when the feed has been silent ≥1 s OR broad
// activity has collapsed (mirrors useFeedFreshness so the yellow dot
// and the click behavior agree), any user click anywhere on the page
// fires one resnapshot. Throttled so rapid clicking can't spam hydra.
const CLICK_REFRESH_STALE_MS = 1_000;
const CLICK_REFRESH_THROTTLE_MS = 2_000;
const CLICK_REFRESH_ACTIVITY_WINDOW_MS = 30_000;
const CLICK_REFRESH_ACTIVITY_THRESHOLD = 0.10;
const CLICK_REFRESH_ACTIVITY_MIN_POP = 20;

export function useHydra() {
  const clientsRef = useRef<HydraClient[]>([]);
  const connected = useMarketStore((s) => s.connected);
  const marketsCount = useMarketStore((s) => s.marketsCount);
  const orderbooksCount = useMarketStore((s) => s.orderbooksCount);

  useEffect(() => {
    const store = () => useMarketStore.getState();

    // Per-feed callbacks: ingest only this feed's owned sports; the firehose
    // alone drives the global connected / backpressure flags. activeSports is
    // filtered to owned sports and keyed by feed id so getActiveSports unions
    // the firehose + edges instead of clobbering.
    const callbacksFor = (feed: FeedConfig): HydraCallbacks => {
      const isFirehose = feed.role === 'firehose';
      return {
        onMarket: (market) => {
          if (feed.accepts(normalizeSport(market.sport ?? ''))) {
            store().ingestMarket(market);
          }
        },
        onOrderbook: (ticker, book) => store().ingestOrderbook(ticker, book),
        onConsensus: (consensus) => store().ingestConsensus(consensus),
        onActiveSports: (sports) => {
          const owned = sports.filter((code) => feed.accepts(normalizeSport(code)));
          store().ingestActiveSports(owned, feed.id);
        },
        onStatus: (isConnected) => {
          if (isFirehose) store().setConnected(isConnected);
        },
        onBackpressure: (level) => {
          if (isFirehose) store().setBackpressureLevel(level);
        },
      };
    };

    const clients = FEEDS.map(
      (feed) => new HydraClient(feed.url, callbacksFor(feed), { workerName: feed.workerName }),
    );
    clientsRef.current = clients;

    // Register feed clients so marketStore can route each view's orderbook
    // needs to the client that owns the ticker's sport (NFL book → edge, MLB →
    // firehose) and replay any needs declared before mount.
    store().setFeedClients(
      FEEDS.map((feed, i) => ({
        id: feed.id,
        client: clients[i],
        accepts: feed.accepts,
        role: feed.role,
      })),
    );
    for (const client of clients) client.start();

    // Fast-path drain on tab close. `pagehide` is more reliable than
    // `beforeunload` (fires on mobile + iOS Safari, doesn't break bfcache)
    // and lets each SharedWorker drop our orderbook needs from the union
    // immediately instead of waiting on its 30 s heartbeat reaper.
    //
    // Bfcache caveat: when `event.persisted` is true the page is being
    // snapshotted for back/forward cache, NOT torn down — calling stop()
    // there permanently orphans the tab. Skip it and rely on the worker's
    // reaper-resurrect path + the tab-side stall detector to recover.
    const handlePageHide = (event: PageTransitionEvent) => {
      if (event.persisted) return;
      for (const client of clients) client.stop();
    };
    window.addEventListener('pagehide', handlePageHide);

    // Belt-and-suspenders for bfcache restore: force a clean restart on
    // resume in case the worker reaped our port during the freeze.
    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      for (const client of clients) client.restartShared('bfcache restore');
    };
    window.addEventListener('pageshow', handlePageShow);

    // Heal stale-on-return per feed: when the tab becomes visible after time
    // in the background, ask each upstream we haven't heard from in
    // VISIBILITY_RESNAP_STALE_MS+ for a fresh markets snapshot. Cheap when
    // fresh; the threshold gates out rapid alt-tabs.
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      for (const client of clients) {
        const lastMs = client.lastMessageMs;
        if (lastMs === 0) continue;
        if (Date.now() - lastMs > VISIBILITY_RESNAP_STALE_MS) client.requestResnapshot();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Watchdog for silent stalls while the tab stays visible — per feed, since
    // one pipe can wedge while another keeps flowing. Skip while hidden to
    // avoid waking a deliberately-throttled background tab.
    const watchdog = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      for (const client of clients) {
        const lastMs = client.lastMessageMs;
        if (lastMs === 0) continue;
        if (Date.now() - lastMs > WATCHDOG_STALE_MS) client.requestResnapshot();
      }
    }, WATCHDOG_INTERVAL_MS);

    // Global click-to-refresh: any user click fires a full reconnect on every
    // feed when the aggregate feed looks stale. Reads marketStore.lastMessageMs
    // (the same source the yellow-dot indicator uses) — it's bumped by ingest
    // from ANY feed, so it reflects the whole live picture. Throttled so a
    // flurry of clicks can't spam upstream. Reconnect (not just resnapshot)
    // because the silent-stall mode is "WS OPEN but no frames flow" — a
    // resnapshot over that socket is itself a lost message.
    let lastClickRefreshAt = 0;
    const handleClickRefresh = () => {
      const s = store();
      const lastMs = s.lastMessageMs;
      if (lastMs === 0) return; // initial load — let the connect snapshot carry it

      const ageMs = Date.now() - lastMs;
      const recentSilence = ageMs > CLICK_REFRESH_STALE_MS;
      const { active, total } = s.getActivityCount(CLICK_REFRESH_ACTIVITY_WINDOW_MS);
      const activityCollapse =
        total >= CLICK_REFRESH_ACTIVITY_MIN_POP &&
        active / total < CLICK_REFRESH_ACTIVITY_THRESHOLD;
      if (!recentSilence && !activityCollapse) return;

      const now = Date.now();
      if (now - lastClickRefreshAt < CLICK_REFRESH_THROTTLE_MS) return;
      lastClickRefreshAt = now;
      for (const client of clients) client.forceReconnect();
    };
    document.addEventListener('pointerdown', handleClickRefresh, { capture: true });

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('pointerdown', handleClickRefresh, { capture: true });
      clearInterval(watchdog);
      store().setFeedClients([]);
      for (const client of clients) client.stop();
      clientsRef.current = [];
    };
  }, []);

  return { connected, marketsCount, orderbooksCount };
}
